import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { DthSessionStorage } from "./DthSessionStorage";
import { DthLoginPasswordCache } from "./DthLoginPasswordCache";
import {
    IDthLoadContactsRequest, IDthPswLoginRequest,
    IDthPswLoginResponse, IDthOtpRequest,
    IDthPublicConfiguration, IDthUserSession, IDthAuthAppInfoResponse,
    IDthAuthAppOtpRequest,
    IDthOauthLoginRequest,
    IDthPrivateConfiguration,
    IDthRefreshValidationTokenRequest,
    IDthRefreshValidationTokenResponse,
    IDthAuthAppInfoRequest,
    IDthResetPasswordRequest,
    IDthLogoutRequest,
    IDthRefreshApiAccessTokenRequest,
    IDthCheckApiAccessTokenResult,
    IDthRefreshApiAccessTokenResponse,
    IDthChangeRequestPasswordRequest,
    IDthChangeRequestPasswordResponse,
    IDthChangePasswordRequest,
    IDthGetUserInfoRequest,
    IDthUser,
    IDthContact,
    GenericResponse,
    IDTHEngagementRoomResponse,
    EngagementRoomLogsResponse,
    IDthRegisterUserRequest,
    IDthVerifyOtpValidateUserRequest,
    IDthRequestOtpValidateUserRequest,
    IDthFirebaseTokenRegisterRequest,
    EngagementRoomLogs
} from "./models/httpClientModels";
import {
    EnvironmentEnum, getCurrentEnvironment, getCurrentTimestamp,
    isNullOrWhitespace
} from "../utils";
import ForceLogoutError from "./errors/ForceLogoutError";
import * as Lifecycle from "../../Lifecycle";
import { hashPassword, matrixIdToParticipantId } from "./utils/utils";
import HttpResponseError from "./errors/HttpResponseError";
import ErrorDialog from "../../components/views/dialogs/ErrorDialog";
import Modal from "../../Modal";
import { _t } from "../../languageHandler";
import { APP_VERSION } from "../constants";
import { IMatrixClientCreds } from "../../MatrixClientPeg";
import DthValidationTokenManager, { type DthStoredValidationToken, DthValidationTokenType } from "./DthValidationTokenManager";

const logger = baseLogger.getChild("DthHttpClient");

interface DthHttpClientOptions {
    baseUrl: string;
}

export class DthHttpClient {
    private static instance: DthHttpClient | null = null;
    private static baseUrl: string | null = null;  // URL statico per inizializzazione
    private axiosClient: AxiosInstance;
    private opts: DthHttpClientOptions;
    /**
     * Promise deduplication per getApiAccessToken
     * Memorizza la Promise dell'API Access Token in corso di ottenimento.
     */
    private _apiAccessTokenPromise: Promise<string> | null = null;

    /**
     * Costruttore privato, per forzare l'uso del singleton.
     * Verifica che i parametri statici siano stati inizializzati.
     * Lancia un errore se si tenta di creare un'istanza senza inizializzazione o se i parametri non sono validi.
     */
    private constructor() {
        if (!DthHttpClient.baseUrl) {
            throw new Error(_t("http_client_errors.base_url_must_be_set_before_creating_the_instance"));
        }
        this.opts = { baseUrl: DthHttpClient.baseUrl };

        this.axiosClient = axios.create({
            baseURL: this.opts.baseUrl,
            timeout: 30000,
            validateStatus: () => true //le eccezioni sugli stati http vengono gestite dall'interceptor
        });

        const environment: EnvironmentEnum = getCurrentEnvironment();

        // Interceptor per aggiungere header comuni a tutte le richieste
        this.axiosClient.interceptors.request.use(
            (config) => {
                const build = "1";
                const version = APP_VERSION;

                // config.headers["User-Agent"] = `GOT/0.4.15-dev (Build 102; sdk_gphone64_arm64 Google; Android 15)`;
                config.headers["Content-Type"] = "application/json";
                config.headers["X-Client-Type"] = "GOT Web";
                config.headers["X-Client-Version"] = `${version}(${build})`;
                config.headers["Environment"] = environment;

                console.debug(`[DthHttpClient] ${config.method?.toUpperCase()} ${config.url}`);
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Interceptor per gestire in modo centralizzato le eccezioni dovuto allo status code della risposta
        this.axiosClient.interceptors.response.use(response => {
            if (response.status >= 200 && response.status <= 299)
                return response;

            let responseErrorText =
                response.data?.error?.message ??
                response.data?.error ??
                response.data ??
                _t("settings.general.generic_error_http", {
                    errorText: response.statusText,
                    httpStatus: response.status,
                });

            if (typeof responseErrorText !== "string") {
                try {
                    responseErrorText = JSON.stringify(responseErrorText);
                } catch (e) {
                    responseErrorText = _t("settings.general.generic_error_http", {
                        errorText: response.statusText,
                        httpStatus: response.status,
                    }); // fallback di sicurezza
                }
            }
            logger.error("Error message: " + responseErrorText)

            throw new HttpResponseError(response.status, responseErrorText, response);
        });

    }

    /**
     * Inizializza i parametri statici necessari per il funzionamento del DthHttpClient. 
     * Deve essere chiamato prima di ottenere l'istanza.
     * @param baseUrl URL di base per le richieste HTTP
     */
    public static initialize(baseUrl: string): void {
        if (DthHttpClient.baseUrl) {
            throw new Error(_t("http_client_errors.api_client_already_initialized"));
        }
        // Rimuove eventuali slash finali dall'URL
        DthHttpClient.baseUrl = baseUrl.replace(/\/+$/, "");
    }

    /**
     * Metodo statico per ottenere l'istanza del DthHttpClient.
     * Se l'istanza non esiste, viene creata.
     * @returns L'istanza del DthHttpClient
     */
    public static getInstance(): DthHttpClient {
        if (!DthHttpClient.instance) {
            DthHttpClient.instance = new DthHttpClient();
        }
        return DthHttpClient.instance;
    }

    //#region Anonymous methods
    /**
     * Recupera la configurazione pubblica
     * @returns IDthPublicConfiguration
     */
    public async getPublicConfiguration(): Promise<IDthPublicConfiguration> {
        try {
            const res: AxiosResponse = await this.axiosClient.get("/v1/common/config/public");

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.get_public_configuration_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }

            return res.data.data as IDthPublicConfiguration;
        } catch (err) {
            logger.error("Error in getPublicConfiguration:", err);
            throw err;
        }
    }

    /**
     * Recupera la configurazione privata, che include feature flag e altre info utili per il funzionamento del client.
     * @returns IDthPrivateConfiguration
     */
    public async resetPassword(resetPsw: IDthResetPasswordRequest): Promise<void> {
        try {
            const email = resetPsw.email;
            if (!email) {
                throw new Error(_t("http_client_errors.email_is_required"));
            }

            const res: AxiosResponse =
                await this.axiosClient.post("/v1/identity/auth/password/reset-request", { email });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.reset_password_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }
            return;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Login email password
     * @param loginParams 
     * @returns 
     */
    public async pswLogin(loginParams: IDthPswLoginRequest): Promise<IDthPswLoginResponse> {
        try {
            const email = loginParams.username;
            const password = loginParams.password;
            if (!email || !password) {
                throw new Error(_t("http_client_errors.email_and_password_are_required"));
            }

            const hashedPassword = loginParams.passwordIsHashed ? password : await hashPassword(password, email);

            // Verifica se è l'utente è abilitato
            let userEnabled = true;

            let res: AxiosResponse;
            try {
                res = await this.axiosClient.post("/v1/identity/auth/login?provider=CREDENTIALS", {
                    email,
                    password: hashedPassword
                });
            } catch (err) {
                if (err instanceof HttpResponseError) {
                    // ATH-004 = user needs validation
                    // se l'utente deve validare l'account ignoro l'errore per gestirlo dopo
                    if (err.statusCode === 401 && err.response.data?.error?.code === "ATH-004") {
                        userEnabled = false;
                        res = err.response;
                    }
                    // ATH-008 = Unable to create validation token
                    // Non è possibile craere un validation token, il precedente non è ancora scaduto
                    // tenta di prelevare il precedente dalla memoria e verificare se è ancora valido, altrimenti propaga l'eccezione
                    else if (err.statusCode === 409 && err.response.data?.error?.code === "ATH-008") {
                        // Se non c'è un token valido, o non c'è proprio, allora propago l'eccezione
                        const lastStoredToken: DthStoredValidationToken | null = await DthValidationTokenManager.getStored(email, DthValidationTokenType.LOGIN);
                        const lastValidationToken = lastStoredToken?.token;
                        if (!lastValidationToken) throw err;
                        // Se non c'è l'informazione sull'authenticator in memoria
                        const authenticatorAppEnabledString: string | null = lastStoredToken.extra?.authenticatorAppEnabled?.toString() ?? null;
                        if (authenticatorAppEnabledString === null) throw err;
                        const authenticatorAppEnabled = authenticatorAppEnabledString === "true";

                        res = {
                            status: 200,
                            data: {
                                success: true,
                                error: null,
                                data: {
                                    validationToken: lastValidationToken,
                                    authenticatorAppEnabled: authenticatorAppEnabled
                                }
                            }
                        } as AxiosResponse<any, any, {}>;
                    } else {
                        //altrimenti propago l'eccezione
                        throw err;
                    }
                } else {
                    //altrimenti propago l'eccezione
                    throw err;
                }
            }

            const result = res.data;
            // Se l'utente è abilitato, ma c'è un errore nel login, lancio eccezione
            if (userEnabled && (!result.success || result.error)) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.login_failed") + ": " + JSON.stringify(result.error),
                    res
                );
            }

            const resultData = {
                userEnabled: userEnabled,
                error: result.error,
                success: result.success,
                validationToken: result.data?.validationToken,
                authenticatorAppEnabled: result.data?.authenticatorAppEnabled || false
            } as IDthPswLoginResponse;

            DthSessionStorage.setGenericItem("username", email);
            await DthLoginPasswordCache.set(email, hashedPassword);

            if (resultData.validationToken) {
                // salvo sia il token che l'informazione sull'authenticator app in memoria, in modo da poterla utilizzare dopo il login per mostrare o nascondere le funzionalità legate all'Authenticator App
                await DthValidationTokenManager.save(resultData.validationToken, email, DthValidationTokenType.LOGIN, true, { authenticatorAppEnabled: resultData.authenticatorAppEnabled });
            }

            if (resultData.authenticatorAppEnabled != null) {
                DthSessionStorage.setGenericItem("authenticatorAppEnabled", resultData.authenticatorAppEnabled.toString());
            }

            return resultData;
        } catch (err) {
            logger.error("Error in pswLogin:", err);
            throw err;
        }
    }

    /**
     * Login con OAUTH2
     * @param loginParams 
     * @returns 
     */
    public async oauthLogin(loginParams: IDthOauthLoginRequest): Promise<IDthUserSession> {
        try {
            const provider = loginParams.provider;
            const accessToken = loginParams.accessToken;
            const idToken = loginParams.idToken;
            if (!accessToken || !idToken || !provider) {
                throw new Error(_t("http_client_errors.provider_accesstoken_and_idtoken_are_required"));
            }

            const res: AxiosResponse =
                await this.axiosClient.post(`/v1/identity/auth/login?provider=${provider}`, {
                    accessToken,
                    idToken
                });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.auth_app_login_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const userSession = res.data.data as IDthUserSession;
            // console.log("[DthHttpClient] USER SESSION: " + JSON.stringify(userSession));
            return userSession;
        } catch (err) {
            logger.error("Error in oauthLogin:", err);
            throw err;
        }
    }

    /**
     * Effettua controllo otp ricevuto tramite o AuthApp o Email/Password
     * @param otpParams 
     * @returns 
     */
    public async otp(otpParams: IDthOtpRequest): Promise<IDthUserSession> {
        try {
            const code: string = otpParams.code;
            const email: string | null = await DthSessionStorage.tryGetUsername();
            const validationToken: string | null = await this.getLastValidationToken(email, DthValidationTokenType.LOGIN);

            if (isNullOrWhitespace(code)) {
                throw new Error(_t("http_client_errors.otp_code_is_required"));
            }

            if (isNullOrWhitespace(validationToken)) {
                throw new Error(_t("http_client_errors.validation_token_is_required"));
            }

            const res: AxiosResponse =
                await this.axiosClient.post("/v1/identity/auth/login/verify", {
                    code,
                    validationToken
                });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.otp_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const userSession: IDthUserSession = res.data.data;
            // Una volta confermato l'otp, il validation token di login non è più valido, quindi lo rimuovo
            await DthValidationTokenManager.remove(userSession.user.email, DthValidationTokenType.LOGIN);

            return userSession;
        } catch (err) {
            logger.error("Error in otp:", err);
            throw err;
        }
    }

    /**
     * Aggiorna l'Api Access Token, tramite l'Api Refresh Token
     * @param params 
     * @returns 
     */
    public async tryRefreshApiAccessToken(params: IDthRefreshApiAccessTokenRequest):
        Promise<IDthRefreshApiAccessTokenResponse> {
        try {
            const refreshToken = params.refreshToken;
            if (!refreshToken) throw new Error(_t("http_client_errors.no_refresh_token_found"));

            const res: AxiosResponse =
                await this.axiosClient.post("/v1/identity/auth/refresh", { refreshToken });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.refresh_validation_token_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.data as IDthRefreshApiAccessTokenResponse;
        } catch (err) {
            logger.error("Error in tryRefreshApiAccessToken:", err);
            throw err;
        }
    }

    /**
     * Registra un nuovo utente
     * @param registerParams 
     * @returns
     */
    public async registerUser(registerParams: IDthRegisterUserRequest): Promise<boolean> {
        try {
            const hashedPassword = await hashPassword(registerParams.password, registerParams.email);
            registerParams.password = hashedPassword;

            const res: AxiosResponse =
                await this.axiosClient.post("/v1/identity/users", registerParams);

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.user_registration_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }
            return res.data.success;
        } catch (err) {
            logger.error("Error in registerUser:", err);
            throw err;
        }
    }

    /**
     * Richiede un OTP per l'abilitazione dell'utente
     * @param email 
     * @returns
     */
    public async requestOtpValidationUser(params: IDthRequestOtpValidateUserRequest): Promise<boolean> {
        try {
            const res: AxiosResponse =
                await this.axiosClient.post(`/v1/identity/users/${params.email}/confirm`);

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.user_request_validation_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }
            return res.data.success;
        } catch (err) {
            logger.error("Error in requestOtpValidationUser:", err);
            throw err;
        }
    }

    /**
     * Valida un OTP per la validazione di un nuovo utente
     * @param email 
     * @returns
     */
    public async verifyOtpValidateUser(params: IDthVerifyOtpValidateUserRequest): Promise<IDthUserSession> {
        try {
            const password = await hashPassword(params.password, params.email);
            const res: AxiosResponse =
                await this.axiosClient.put(`/v1/identity/users/${params.email}/verify`, { code: params.code, password: password });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.user_validation_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const userSession = res.data.data as IDthUserSession;

            // Salvo l'informazione che indica se l'utente ha l'Authenticator App abilitato, 
            // in modo da poterla utilizzare dopo il login per mostrare o nascondere le funzionalità legate all'Authenticator App
            if (userSession.user.authenticatorAppEnabled != null) {
                DthSessionStorage.setGenericItem("authenticatorAppEnabled", userSession.user.authenticatorAppEnabled.toString());
            }

            return userSession;
        } catch (err) {
            logger.error("Error in verifyOtpValidateUser:", err);
            throw err;
        }
    }

    //#endregion

    //#region Auth methods

    public async authenticatedRequest(path: string, options: AxiosRequestConfig = {}, apiAccessToken: string | undefined = undefined): Promise<AxiosResponse> {
        try {
            const token = apiAccessToken ?? await this.getApiAccessToken();
            // console.log("[DthHttpClient] API ACCESS TOKEN AUTH REQUEST: " + token);
            const headers = { ...(options.headers || {}) };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            // Passa opzioni aggiornate con header che includono Authorization
            return this.axiosClient.request({ url: path, ...options, headers });
        } catch (err) {
            if (err instanceof ForceLogoutError) {
                Modal.createDialog(ErrorDialog, {
                    title: _t("auth.session_logged_out_title"),
                    description: _t("auth.session_logged_out_description")
                },
                    undefined,
                    undefined,
                    undefined,
                    {
                        onBeforeClose: async (_): Promise<boolean> => {
                            logger.warn("ForceLogoutError caught, logging out and redirecting to login page");
                            // FORZA LOGOUT
                            Lifecycle.logout();
                            return Promise.resolve(true);
                        }
                    }
                );
            }
            throw err;
        }
    }

    public async getAuthAppInfo(authAppParams: IDthAuthAppInfoRequest): Promise<IDthAuthAppInfoResponse> {
        let email: string | null = null;
        try {
            email = await DthSessionStorage.tryGetUsername();
            const validationToken: string | null = await this.getLastValidationToken(email, DthValidationTokenType.AUTH_APP);

            if (isNullOrWhitespace(validationToken)) {
                throw new Error(_t("http_client_errors.validation_token_is_required"));
            }

            let res: AxiosResponse;
            try {
                res = await this.authenticatedRequest(`/v1/identity/auth/authenticator`, {
                    method: "POST",
                    data: { code: authAppParams.code ?? null, validationToken }
                });
            } catch (err) {
                if (err instanceof HttpResponseError) {
                    // ATH-006 = Authenticator already exists
                    // Impossibile recuperare le informazioni dell'Auth App:
                    // probabilmente nella sessione esiste già un'Auth App registrata o un token precedente ancora attivo.
                    // Se trovo un validation token valido con le informazioni dell'Auth App lo restituisco, altrimenti propago l'eccezione.
                    if (err.statusCode === 409 && err.response.data?.error?.code === "ATH-006") {
                        // Se non c'è un token valido, o non c'è proprio, allora propago l'eccezione
                        const lastStoredToken: DthStoredValidationToken | null = await DthValidationTokenManager.getStored(email!, DthValidationTokenType.AUTH_APP);
                        const lastValidationToken = lastStoredToken?.token;
                        // Se non ci sono le informazioni sull'authenticator in memoria, propago l'eccezione
                        if (!lastValidationToken || !lastStoredToken || !lastStoredToken.extra) throw err;
                        const authAppInfo: IDthAuthAppInfoResponse = lastStoredToken.extra as IDthAuthAppInfoResponse;
                        if (!authAppInfo.secret || !authAppInfo.otpAuthUrl) throw err;

                        res = {
                            status: 200,
                            data: {
                                success: true,
                                error: null,
                                data: authAppInfo
                            }
                        } as AxiosResponse<any, any, {}>;
                    } else {
                        //altrimenti propago l'eccezione
                        throw err;
                    }
                } else {
                    //altrimenti propago l'eccezione
                    throw err;
                }
            }

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.get_auth_app_info_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const data = res.data.data as IDthAuthAppInfoResponse;

            // Vengono salvati in memoria sia il validation token che le informazioni per la registrazione dell'authenticator app, 
            // in modo da poter riutilizzarli nel caso dovessero essere utilizzate successivamente prima della loro scadenza
            await DthValidationTokenManager.save(validationToken!, email!, DthValidationTokenType.AUTH_APP, true, data);

            return data;
        } catch (err) {
            logger.error("Error in getAuthAppInfo:", err);
            throw err;
        }
    }

    public async registerAuthAppOtp(otpParams: IDthAuthAppOtpRequest): Promise<boolean> {
        try {
            const code: string = otpParams.code;
            const email: string | null = await DthSessionStorage.tryGetUsername();
            const validationToken: string | null = await this.getLastValidationToken(email, DthValidationTokenType.AUTH_APP);

            if (isNullOrWhitespace(code)) {
                throw new Error(_t("http_client_errors.auth_app_otp_code_is_required"));
            }

            if (isNullOrWhitespace(validationToken)) {
                throw new Error(_t("http_client_errors.validation_token_is_required"));
            }

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/authenticator`, {
                method: "PUT",
                data: { code, validationToken }
            });

            const statusOK: boolean = res.status === 200;
            if (statusOK) {
                // Una volta confermato l'otp, il validation token per l'auth app non è più valido, quindi lo rimuovo
                await DthValidationTokenManager.remove(email!, DthValidationTokenType.AUTH_APP);
            }
            return statusOK;
        } catch (err) {
            logger.error("Error in registerAuthAppOtp:", err);
            throw err;
        }
    }

    public async removeAuthAppLogin(otpParams: IDthAuthAppOtpRequest): Promise<boolean> {
        try {
            const code: string = otpParams.code;
            const email: string | null = await DthSessionStorage.tryGetUsername();
            const validationToken: string | null = await this.getLastValidationToken(email, DthValidationTokenType.AUTH_APP);

            if (isNullOrWhitespace(otpParams.password || "")) {
                throw new Error(_t("http_client_errors.password_is_required"));
            }

            if (isNullOrWhitespace(code)) {
                throw new Error(_t("http_client_errors.auth_app_otp_code_is_required"));
            }

            if (isNullOrWhitespace(validationToken)) {
                throw new Error(_t("http_client_errors.validation_token_is_required"));
            }

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/authenticator`, {
                method: "DELETE",
                data: { code, validationToken }
            });

            const statusOK: boolean = res.status === 200;
            // Non bisogna rimuovere il token in questo caso, perché per la rimozione dell'auth app non invalida il vecchio token
            // if (statusOK) {
            //     // Una volta confermato l'otp, il validation token per l'auth app non è più valido, quindi lo rimuovo
            //     await DthValidationTokenManager.remove(email!, DthValidationTokenType.AUTH_APP);
            // }
            return statusOK;
        } catch (err) {
            logger.error("Error in removeAuthAppLogin:", err);
            throw err;
        }
    }

    public async changeRequestPassword(changeRequestPasswordParams: IDthChangeRequestPasswordRequest): Promise<IDthChangeRequestPasswordResponse> {
        try {
            const password: string = changeRequestPasswordParams.password;
            if (isNullOrWhitespace(password)) {
                throw new Error(_t("http_client_errors.password_is_required"));
            }

            //L'username con cui viene fatto il login è l'email
            const username = DthSessionStorage.getCurrentUserInfo()!.email;
            const hashedPassword = await hashPassword(password, username ?? "");

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/password/change-request`, {
                method: "POST",
                data: { password: hashedPassword }
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.change_request_password_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.data as IDthChangeRequestPasswordResponse;
        } catch (err) {
            logger.error("Error in changeRequestPassword:", err);
            throw err;
        }
    }

    public async changePassword(changePasswordParams: IDthChangePasswordRequest): Promise<boolean> {
        try {
            const currentPassword: string = changePasswordParams.currentPassword;
            const newPassword: string = changePasswordParams.newPassword;
            const code: string = changePasswordParams.code;
            if (isNullOrWhitespace(currentPassword)) {
                throw new Error(_t("http_client_errors.current_password_is_required"));
            }
            if (isNullOrWhitespace(newPassword)) {
                throw new Error(_t("http_client_errors.new_password_is_required"));
            }
            if (isNullOrWhitespace(code)) {
                throw new Error(_t("http_client_errors.code_is_required"));
            }

            const username = DthSessionStorage.getCurrentUserInfo()!.email;
            const hashedCurrentPassword = await hashPassword(currentPassword, username);
            const hashedNewPassword = await hashPassword(newPassword, username);

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/password`, {
                method: "PUT",
                data: { currentPassword: hashedCurrentPassword, newPassword: hashedNewPassword, code }
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.change_password_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.success;
        } catch (err) {
            logger.error("Error in changePassword:", err);
            throw err;
        }
    }

    public async loadContacts(params: IDthLoadContactsRequest): Promise<IDthContact[]> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/users/${params.apiUserId}/contacts`, {
                method: "GET",
                params,
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.load_contacts_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.data as IDthContact[];
        } catch (err) {
            logger.error("Error in loadContacts:", err);
            throw err;
        }
    }

    public async getUserInfo(params: IDthGetUserInfoRequest): Promise<IDthUser> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/users/${params.apiUserId}`, {
                method: "GET",
                params,
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.get_user_info_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.data as IDthUser;
        } catch (err) {
            logger.error("Error in getUserInfo:", err);
            throw err;
        }
    }

    public async registerFirebaseToken(params: IDthFirebaseTokenRegisterRequest): Promise<boolean> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/users/${params.userId}/devices`, {
                method: "POST",
                data: {
                    client: params.client,
                    displayName: params.displayName,
                    pushKey: params.pushKey,
                    voipKey: params.voipKey,
                    deviceId: params.deviceId,
                    lang: params.lang
                }
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.register_firebase_token_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            return res.data.success;
        } catch (err) {
            logger.error("Error in registerFirebaseToken:", err);
            throw err;
        }
    }

    public async getPrivateConfiguration(): Promise<IDthPrivateConfiguration> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/users/me/config`, {
                method: "GET",
            });

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.get_private_configuration_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const privateConf = res.data.data;
            return privateConf;
        } catch (err) {
            logger.error("Error in getPrivateConfiguration:", err);
            throw err;
        }
    }

    public async tryRefreshValidationToken(params: IDthRefreshValidationTokenRequest): Promise<GenericResponse<IDthRefreshValidationTokenResponse>> {
        let username: string | null = null;
        try {
            username = await DthSessionStorage.tryGetUsername();
            if (!username) {
                throw new Error(_t("http_client_errors.email_is_required"));
            }

            const password = params.password;
            if (!password) {
                throw new Error(_t("http_client_errors.password_is_required"));
            }
            const hashedPassword = params.passwordIsHashed ? password : await hashPassword(password, username);

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/validation`,
                {
                    method: "POST",
                    data: { password: hashedPassword },
                },
                params.apiAccessToken
            );

            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.refresh_validation_token_failed") + ": " + JSON.stringify(res.data.error),
                    res);
            }

            const data = res.data.data as IDthRefreshValidationTokenResponse;
            // Salvo il nuovo validation token
            await DthValidationTokenManager.save(data.validationToken, username, DthValidationTokenType.AUTH_APP, true);
            if (data.authenticatorAppEnabled != null) {
                DthSessionStorage.setGenericItem("authenticatorAppEnabled", data.authenticatorAppEnabled.toString());
            }

            return res.data;
        } catch (err) {
            if (err instanceof HttpResponseError) {
                // se il validation token non può essere aggiornato 
                // perchè è ancora valido il corrente,
                // non ritorno l'eccezione ma i valori precedenti
                if (err.statusCode == 409 && (err.response?.data?.error?.code ?? "") == "ATH-008") {
                    return {
                        data: {
                            authenticatorAppEnabled: DthSessionStorage.getGenericItem("authenticatorAppEnabled") || "false",
                            validationToken: await DthValidationTokenManager.get(username ?? "", DthValidationTokenType.AUTH_APP) ?? ""
                        },
                        success: false,
                        error: err.message
                    };
                }
            }
            logger.error("Error in tryRefreshValidationToken:", err);
            throw err;
        }
    }

    public async disconnectAllSessions(): Promise<boolean> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/sessions`, {
                method: "DELETE",
            });

            return res.status === 200;
        } catch (err) {
            logger.error("Error in disconnectAllSessions:", err);
            throw new Error(_t("http_client_errors.disconnect_all_sessions_failed"));
        }
    }

    public async logout(logoutParams: IDthLogoutRequest): Promise<boolean> {
        try {
            const deviceId: string | null = logoutParams.deviceId;
            // console.log("[DthHttpClient] DEVICE ID REQUEST: " + deviceId);

            if (isNullOrWhitespace(deviceId)) {
                throw new Error(_t("http_client_errors.device_id_is_required"));
            }

            const res: AxiosResponse = await this.authenticatedRequest(`/v1/identity/auth/logout`, {
                method: "DELETE",
                data: { deviceId }
            });

            return res.status === 200;
        } catch (err) {
            logger.error("Error in logout:", err);
            throw new Error(_t("http_client_errors.logout_failed"));
        }
    }


    // #region Engagement Calls

    /**
     * Crea una chiamata in uscita
     * @param participantsIds lista di matrix Id partecipanti (senza il chiamante, solo la parte tra @ e :)
     * @param isVideo true se video, false se solo audio
     */
    public async createEngagementRoom(participantsIds: string[], isVideo: boolean = false): Promise<IDTHEngagementRoomResponse> {
        try {
            // Mappa i matrix Id per estrarre solo la parte tra @ e :
            const mappedParticipantsIds = participantsIds.map(id => {
                return matrixIdToParticipantId(id);
            });

            const res: AxiosResponse = await this.authenticatedRequest("/v1/engagement/rooms", {
                method: "POST",
                data: {
                    participantsIds: mappedParticipantsIds,
                    isVideo
                }
            });
            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.engagement.create_room_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }
            return res.data.data;
        } catch (err) {
            logger.error("Error in createEngagementRoom:", err);
            throw err;
        }
    }

    /**
     * Accetta una chiamata ricevuta
     * @param invitationId id invito ricevuto da notifica
     */
    public async joinEngagementRoom(invitationId: string): Promise<IDTHEngagementRoomResponse> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest("/v1/engagement/rooms/join", {
                method: "POST",
                data: { invitationId }
            });
            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.engagement.join_room_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }
            return res.data.data;
        } catch (err) {
            logger.error("Error in joinEngagementRoom:", err);
            throw err;
        }
    }

    /**
     * Chiude la chiamata (cancel)
     * @param roomId id della stanza
     */
    public async cancelEngagementRoom(roomId: string): Promise<boolean> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest("/v1/engagement/rooms/cancel", {
                method: "PUT",
                data: { roomId }
            });
            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.engagement.cancel_room_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }
            return res.data.success;
        } catch (err) {
            logger.error("Error in cancelEngagementRoom:", err);
            throw err;
        }
    }

    /**
     * Rifiuta la chiamata (reject)
     * @param invitationId id invito ricevuto da notifica
     */
    public async rejectEngagementRoom(invitationId: string): Promise<boolean> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest("/v1/engagement/rooms/reject", {
                method: "PUT",
                data: { invitationId }
            });
            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.engagement.reject_room_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }
            return res.data.success;
        } catch (err) {
            logger.error("Error in rejectEngagementRoom:", err);
            throw err;
        }
    }

    /**
     * Registro chiamate (logs)
     * Versione legacy: ritorna un array di log (EngagementRoomLogs[])
     * Versione corrente: EngagementRoomLogsResponse
     */
    public async getEngagementRoomLogs(): Promise<EngagementRoomLogsResponse | EngagementRoomLogs[]> {
        try {
            const res: AxiosResponse = await this.authenticatedRequest("/v1/engagement/rooms/logs", {
                method: "GET",
            });
            if (!res.data.success || res.data.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.engagement.get_logs_failed") + ": " + JSON.stringify(res.data.error),
                    res
                );
            }
            return res.data.data;
        } catch (err) {
            logger.error("Error in getEngagementRoomLogs:", err);
            throw err;
        }
    }

    //#endregion

    //#endregion

    //#region Utils

    //#region API ACCESS TOKEN MANAGEMENT
    /**
     * Prova a prelevare l'api access token, se non è possibile prelevarlo o rigenerarlo ritorna undefined 
     * (es. token non trovato, token scaduto e refresh fallito, errori imprevisti)
     * @returns 
     */
    private async tryGetApiAccessToken(): Promise<string | undefined> {
        try {
            return await this.getApiAccessToken();
        } catch {
            return undefined;
        }
    }

    /**
     * Prova a prelevare l'api access token, se non è possibile prelevarlo o rigenerarlo lancia ForceLogoutError
     * @returns api access token valido o lancia ForceLogoutError se non è possibile prelevarlo o rigenerarlo
     */
    private async getApiAccessToken(): Promise<string> {
        // Se una richiesta è già in corso, restituisci la stessa Promise
        if (this._apiAccessTokenPromise) {
            logger.debug("getApiAccessToken: returning existing promise for ongoing token retrieval/refresh");
            return this._apiAccessTokenPromise;
        }
        // Crea una nuova Promise e salvala
        this._apiAccessTokenPromise = (async () => {
            try {
                // Prelieva token dalla sessione, se non c'è o è da aggiornare forza aggiornamento, 
                // se l'aggiornamento fallisce forza logout
                let apiAccessToken: string | undefined = await this.retrieveApiAccessTokenFromSession();
                if (!apiAccessToken) {
                    throw new ForceLogoutError(_t("http_client_errors.api_access_token_not_found"));
                }

                // Controlla se il token è da aggiornare, se sì aggiorna, altrimenti ritorna quello corrente
                if (await this.isApiAccessTokenToUpdate(apiAccessToken)) {
                    logger.warn("API access token is due for refresh, refreshing...");
                    const matrixCreds = await this.updateApiAccessToken();
                    apiAccessToken = matrixCreds.apiAccessToken;
                }
                return apiAccessToken;
            } catch (err) {
                const prefixMessageError = "Error retrieving Api Access Token:";
                console.error(`[DthHttpClient] ${prefixMessageError}`, err);
                const message = `${prefixMessageError} ${JSON.stringify(err)}`;
                throw new ForceLogoutError(message);
            } finally {
                // Reset la promise solo dopo che si è risolta o ha fallito
                this._apiAccessTokenPromise = null;
            }
        })();
        return this._apiAccessTokenPromise;
    }

    /**
     * Preleva l'api access token dalla sessione
     */
    private async retrieveApiAccessTokenFromSession(): Promise<string | undefined> {
        const currentApiAccessToken = await DthSessionStorage.getApiAccessToken();
        return currentApiAccessToken || undefined;
    }

    /**
     * Verifica se l'api access token deve essere aggiornato
     * @param apiAccessToken token da controllare, se non passato viene preso dalla sessione
     * @return true se l'api access token deve essere aggiornato, false altrimenti
     */
    public async isApiAccessTokenToUpdate(apiAccessToken?: string): Promise<boolean> {
        const checkApiAccessTokenResult = await this.checkGenerationApiAccessToken(apiAccessToken);
        return !!checkApiAccessTokenResult.apiAccessToken && checkApiAccessTokenResult.toUpdate;
    }

    /**
     * Aggiorna l'api access token usando il refresh token
     * @returns le nuove credenziali del client Matrix
     */
    public async updateApiAccessToken(): Promise<IMatrixClientCreds> {
        //prelievo del refresh token
        const apiRefreshToken = await DthSessionStorage.getApiRefreshToken();
        if (!apiRefreshToken) {
            throw new ForceLogoutError(_t("http_client_errors.api_refresh_token_not_found"));
        }

        const refreshApiAccessTokenResponse = await this.tryRefreshApiAccessToken({ refreshToken: apiRefreshToken });
        // AGGIORNA CREDENZIALI E RESTORE SESSION
        // const homeserverUrl = DthMatrixSessionStorage.getGenericItem(HOMESERVER_URL_KEY);
        const matrixCreds = await DthSessionStorage.getCredentials();
        matrixCreds.apiAccessToken = refreshApiAccessTokenResponse.accessToken;
        matrixCreds.apiRefreshToken = refreshApiAccessTokenResponse.refreshToken;
        matrixCreds.apiExpirationToken = refreshApiAccessTokenResponse.expirationDate;
        await DthSessionStorage.saveCredentials(matrixCreds);
        logger.debug("Api Access Token refreshed successfully");
        // ATTENZIONE: Essendo che è cambiato solo l'API access token e non quello Matrix non c'è bisogno di ricerare il matrixClient
        return matrixCreds;
    }

    /**
     * Controlla se il access token bisogna (ri)generarlo
     * @param apiAccessToken token da controllare, se non passato viene preso dalla sessione
     * @returns Risultato del controllo
     */
    private async checkGenerationApiAccessToken(apiAccessToken?: string): Promise<IDthCheckApiAccessTokenResult> {
        const currentApiAccessToken = apiAccessToken || (await this.retrieveApiAccessTokenFromSession());
        const apiExpirationTokenTimestamp = DthSessionStorage.getApiExpirationToken() * 1000;
        return {
            apiAccessToken: currentApiAccessToken || undefined,
            toUpdate: !!currentApiAccessToken && (apiExpirationTokenTimestamp < getCurrentTimestamp())
        };
    }

    //#endregion

    //#region API VALIDATION TOKEN MANAGEMENT
    /**
     * Prova a prelevare l'ultimo validation token, se non presente o scaduto, prova a rigenerarlo, altrimenti ritorna null
     * @param userId id dell'utente a cui è associato il token, se non passato viene prelevato dalla sessione, se non presente nella sessione ritorna null
     * @param type tipo di validation token da prelevare
     * @param password password necessaria per rigenerare il validation token, se non passata viene prelevata dalla sessione, se non presente nella sessione ritorna null
     * @param isHashedPassword indica se la password fornita è già hashata
     * @returns validation token valido o null se non è stato possibile prelevarlo o rigenerarlo
     */
    private async getLastValidationToken(
        userId: string | null,
        type: DthValidationTokenType,
        password?: string,
        isHashedPassword: boolean = false
    ): Promise<string | null> {
        try {
            const validationToken: string | null = userId ? await DthValidationTokenManager.get(userId, type) : null;
            // se c'è un validation token valido, viene restituito
            if (!isNullOrWhitespace(validationToken)) {
                logger.debug(`Found existing validation token for user ${userId} and type ${type}, returning it`);
                return validationToken;
            }

            logger.debug(`No existing validation token found for user ${userId} and type ${type}, attempting to generate a new one`);

            // se non c'è un validation token valido, si prova a rigenerarlo, se non è possibile rigenerarlo viene ritornato null
            const username: string | null = await DthSessionStorage.tryGetUsername();
            if (!username) {
                throw new Error(_t("http_client_errors.email_is_required"));
            }
            
            // Se la password non è stata passata come parametro, provo a prenderla dalla cache, 
            // se non c'è ritorno null perché non è possibile rigenerare il token senza password
            let psw: string | undefined = password;
            let passwordIsHashed: boolean = isHashedPassword;
            if (isNullOrWhitespace(psw)) {
                psw = await DthLoginPasswordCache.get(username) ?? undefined;
                // se prendo la password dalla cache, è hashata (NON DEVE MAI ESSERE SALVATA IN CHIARO), 
                // quindi imposto anche il flag che indica che la password è hashata
                passwordIsHashed = true; 
            }

            // se non c'è la password, non è possibile rigenerare il token, quindi lancio l'eccezione e ritorno null
            if (isNullOrWhitespace(psw)) {
                throw new Error(_t("http_client_errors.password_is_required"));
            }

            // Only fetch api access token when needed (non-LOGIN flows).
            const accessToken = type !== DthValidationTokenType.LOGIN
                ? (await this.tryGetApiAccessToken() ?? "")
                : "";

            // AUTH_APP or other non-login validation tokens -> refresh using API access token
            if (accessToken && psw) {
                // console.log("[DthHttpClient] Generazione nuovo validationToken con accessToken");
                const result = await this.tryRefreshValidationToken({ password: psw, passwordIsHashed: passwordIsHashed });
                logger.debug(`Validation token refresh ${result.success ? "succeeded" : "failed"}`);
                return result.data.validationToken;
            }
            // LOGIN flow -> perform password login
            else if (username && psw) {
                // console.log("[DthHttpClient] Generazione nuovo validationToken con username e password");
                const result = await this.pswLogin({ username, password: psw, passwordIsHashed: passwordIsHashed });
                logger.debug(`Password login for validation token generation ${result.success ? "succeeded" : "failed"}`);
                return result.validationToken ?? null;
            }
        } catch (err) {
            logger.error("Error in getLastValidationToken: ", err);
        }

        logger.warn(`Unable to retrieve or refresh validation token for user ${userId} and type ${type}`);
        return null;
    }

    //#endregion

    //#endregion
}

